import { useMemo } from "react";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import { Box, Checkbox, FormControlLabel, FormGroup, Grid, Stack, Tooltip, Typography } from "@mui/material";
import IconButton from "components/@extended/IconButton";
import Selector from "components/select/selector";
import { ActivityBiomassScreening } from "types/firestoreFeatures";
import { useTranslation } from "utils/locales/utilityFunctions";
import { BIOMASS_CATEGORIES } from "../options";

type VisibleFields = { category: boolean; intervention: boolean; riskAssessment: boolean };

type BiomassScreeningCoreFieldsProps = {
  data: ActivityBiomassScreening;
  setData: (change: { value: any; key: string }) => void;
  inputDisabled: boolean;
  isPrinting?: boolean;
  loading?: boolean;
  showCertificateFields?: boolean;
  visibleFields?: VisibleFields;
  onAdditionalInfoUpload?: (categories: string[]) => void;
};

const BiomassScreeningCoreFields = ({
  data,
  setData,
  inputDisabled,
  isPrinting = false,
  loading = false,
  showCertificateFields = false,
  visibleFields = { category: true, intervention: true, riskAssessment: true },
  onAdditionalInfoUpload,
}: BiomassScreeningCoreFieldsProps) => {
  const { translate } = useTranslation();
  const disabled = inputDisabled || isPrinting;
  const columnWidth = 12 / (showCertificateFields ? 4 : 3);
  const biomassCategories = useMemo(
    () => BIOMASS_CATEGORIES.map((id) => ({ id, label: translate(`biomass-project-form-category-${id}`) })),
    [translate("render-change")],
  );
  const biomassTypes = useMemo<Record<string, { id: string; label: string; tooltip: string }[]>>(
    () => ({
      agriculture: [
        { id: "energyWood", label: translate("biomass-project-form-type-energyWood"), tooltip: translate("biomass-project-form-type-energyWood-tooltip") },
        { id: "agricultureResidues", label: translate("biomass-project-form-type-agricultureResidues"), tooltip: translate("biomass-project-form-type-agricultureResidues-tooltip") },
      ],
      forest: [
        { id: "roundwood", label: translate("biomass-project-form-type-roundwood"), tooltip: translate("biomass-project-form-type-roundwood-tooltip") },
        { id: "clearcutEnergyWood", label: translate("biomass-project-form-type-clearcutEnergyWood"), tooltip: translate("biomass-project-form-type-clearcutEnergyWood-tooltip") },
        { id: "residues", label: translate("biomass-project-form-type-residues"), tooltip: translate("biomass-project-form-type-residues-tooltip") },
      ],
      industryWaste: [{ id: "cleanWaste", label: translate("biomass-project-form-type-cleanWaste"), tooltip: translate("biomass-project-form-type-cleanWaste-tooltip") }],
      infrastructure: [{ id: "infrastructure", label: translate("biomass-project-form-type-infrastructure"), tooltip: translate("biomass-project-form-type-infrastructure-tooltip") }],
      nonforest: [{ id: "residuesNonforest", label: translate("biomass-project-form-type-residuesNonforest"), tooltip: translate("biomass-project-form-type-residuesNonforest-tooltip") }],
    }),
    [translate("render-change")],
  );
  const riskAssessments = useMemo<Record<string, { id: string; label: string; tooltip?: string }[]>>(
    () => ({
      agriculture: [],
      forest: [
        { id: "certified", label: translate("biomass-project-form-supplementary-certified") },
        { id: "notCertified", label: translate("biomass-project-form-supplementary-notCertified") },
      ],
      industryWaste: [{ id: "certified", label: translate("biomass-project-form-supplementary-certified") }],
      infrastructure: [{ id: "additionalInfo", label: translate("biomass-project-form-supplementary-additionalInfo"), tooltip: translate("biomass-project-form-supplementary-additionalInfo-tooltip") }],
      nonforest: [
        { id: "naturecareS3", label: translate("biomass-project-form-supplementary-naturecareS3") },
        { id: "shelterbelt", label: translate("biomass-project-form-supplementary-shelterbelt") },
      ],
    }),
    [translate("render-change")],
  );

  return (
    <>
      {visibleFields.category && (
        <Grid item xs={12} sm={columnWidth}>
          <Typography color="secondary" sx={{ mb: 1 }}>{translate("biomass-project-form-category-label")}</Typography>
          <Selector title={translate("biomass-project-form-category-label")} value={data?.category || ""} options={biomassCategories} label={true} fullWidth={true} changeHandler={(event) => setData({ value: event.target.value, key: "category" })} required={true} disabled={disabled} />
        </Grid>
      )}

      {visibleFields.intervention && data?.category && (
        <Grid item xs={12} sm={columnWidth}>
          <Typography color="secondary">{translate("biomass-project-form-type-label")}</Typography>
          <FormGroup sx={{ pl: 1.3 }}>
            {biomassTypes[data.category]?.map((option) => (
              <Tooltip key={option.id} title={option.tooltip} placement="left"><FormControlLabel control={<Checkbox checked={data.type === option.id} onChange={() => setData({ value: option.id, key: "type" })} />} disabled={biomassTypes[data.category]?.length === 1 || disabled} label={option.label} /></Tooltip>
            ))}
          </FormGroup>
        </Grid>
      )}

      {visibleFields.riskAssessment && data?.category && riskAssessments[data.category]?.length > 0 && (
        <Grid item xs={12} sm={columnWidth}>
          <Typography color="secondary">{translate("biomass-project-form-risk-label")}</Typography>
          <FormGroup sx={{ pl: 1.3 }}>
            {riskAssessments[data.category].map((option) => (
              <Stack key={option.id} direction="row"><Tooltip title={option.tooltip} placement="left"><FormControlLabel control={<Checkbox checked={data.riscAssessment?.includes(option.id)} onChange={() => setData({ value: option.id, key: "riscAssessment" })} />} disabled={(riskAssessments[data.category].length === 1 && data.type !== "energyWood") || disabled} label={option.label} /></Tooltip>
              {option.id === "additionalInfo" && onAdditionalInfoUpload && <Box sx={{ ml: 1 }}><Tooltip title={translate("biomass-project-form-upload-doc-tooltip")}><span><IconButton variant="light" color="secondary" onClick={() => onAdditionalInfoUpload(["screening", data.category!, data.type!, option.id])} disabled={loading || disabled}><FileUploadRoundedIcon fontSize="small" color={loading || disabled ? "disabled" : "primary"} /></IconButton></span></Tooltip></Box>}
              </Stack>
            ))}
          </FormGroup>
        </Grid>
      )}
    </>
  );
};

export default BiomassScreeningCoreFields;
